
import React, { useState, useEffect, useCallback } from 'react';
import { Payment } from '@/services/types';
import { DataTable } from '@/components/DataTable';
import { PaymentActions } from './PaymentActions';
import { ConnectionErrorAlert } from './ConnectionErrorAlert';
import { getPaymentColumns } from './PaymentTableColumns';
import { fetchPaymentData, fetchPaymentDataPaginated, PaymentPaginationParams } from './PaymentDataService';
import { PaymentStatsCard } from './components/PaymentStatsCard';
import { toast } from '@/components/ui/sonner';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { UpcomingPaymentsPanel } from './components/UpcomingPaymentsPanel';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, Database, List, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import debounce from 'lodash.debounce';

const PaymentsPage: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'supabase' | 'mock'>('supabase'); 
  const [fetchCount, setFetchCount] = useState(0);
  const navigate = useNavigate();
  
  // Состояние для диалога подтверждения удаления
  const [paymentToDelete, setPaymentToDelete] = useState<Payment | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Состояние для отображения панели предстоящих платежей
  const [showUpcomingPayments, setShowUpcomingPayments] = useState<boolean>(() => {
    const saved = localStorage.getItem('admin-show-upcoming-payments');
    return saved !== null ? saved === 'true' : true;
  });
  
  // Состояние для пагинации
  const [usePagination, setUsePagination] = useState<boolean>(() => {
    const saved = localStorage.getItem('admin-payments-pagination');
    return saved !== null ? saved === 'true' : true; // По умолчанию включаем пагинацию
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const pageSize = 50;
  
  // Для пагинации фильтрация происходит на сервере, для обычного режима - на клиенте
  const filteredPayments = usePagination 
    ? payments // Уже отфильтровано на сервере
    : (statusFilter ? payments.filter(payment => payment.status === statusFilter) : payments);

  // Загружаем данные платежей
  useEffect(() => {
    fetchPayments();
  }, [fetchCount, usePagination, currentPage, searchQuery, statusFilter]);

  const fetchPayments = async () => {
    setLoading(true);
    
    try {
      if (usePagination) {
        // Пагинированная загрузка
        const params: PaymentPaginationParams = {
          page: currentPage,
          pageSize,
          searchQuery: searchQuery.trim() || undefined,
          statusFilter: statusFilter || undefined
        };
        
        const result = await fetchPaymentDataPaginated(params);
        
        setPayments(result.payments);
        setTotalCount(result.totalCount);
        setHasNextPage(result.hasNextPage);
        setHasPreviousPage(result.hasPreviousPage);
        setDataSource(result.dataSource);
        setConnectionError(result.connectionError);
      } else {
        // Полная загрузка (существующий функционал)
        const result = await fetchPaymentData();
        
        setPayments(result.payments);
        setTotalCount(result.payments.length);
        setDataSource(result.dataSource);
        setConnectionError(result.connectionError);
      }
    } catch (error: any) {
      setConnectionError(error.message || 'Неизвестная ошибка');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshData = () => {
    setFetchCount(prev => prev + 1);
  };

  const handleViewRental = (rentalId: string) => {
    navigate(`/admin/rentals?id=${rentalId}`);
  };
  
  const handleDownloadReceipt = (payment: Payment) => {
    if (payment.receiptUrl) {
      window.open(payment.receiptUrl, '_blank');
    } else {
      toast.info('Чек для этого платежа отсутствует');
    }
  };
  
  const handleOpenInvoice = (paymentId: string, paymentMethod?: string) => {
    if (paymentMethod === 'card') {
      const yoomoneyUrl = `https://yoomoney.ru/checkout/payments/v2/contract?orderId=${paymentId}`;
      window.open(yoomoneyUrl, '_blank');
    } else {
      const novaUrl = `https://sp1-nova.ru/api/pay/?subdomain=tuktukrent.amocrm.ru&invoice_id=${paymentId}`;
      window.open(novaUrl, '_blank');
    }
  };
  
  const handleDeletePayment = (payment: Payment) => {
    setPaymentToDelete(payment);
    setIsDeleteDialogOpen(true);
  };
  
  const toggleUpcomingPayments = () => {
    const newValue = !showUpcomingPayments;
    setShowUpcomingPayments(newValue);
    localStorage.setItem('admin-show-upcoming-payments', String(newValue));
  };
  
  const togglePagination = () => {
    const newValue = !usePagination;
    setUsePagination(newValue);
    localStorage.setItem('admin-payments-pagination', String(newValue));
    setCurrentPage(1); // Сбрасываем на первую страницу
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Дебаунсированный поиск для серверной пагинации
  const debouncedSearchChange = useCallback(
    debounce((query: string) => {
      setSearchQuery(query);
      setCurrentPage(1); // Сбрасываем на первую страницу при поиске
    }, 500),
    []
  );

  const handleSearchChange = (query: string) => {
    if (usePagination) {
      // Для пагинированного режима используем дебаунсированный поиск
      debouncedSearchChange(query);
    } else {
      // Для обычного режима сразу обновляем
      setSearchQuery(query);
      setCurrentPage(1);
    }
  };
  
  const confirmDeletePayment = async () => {
    if (!paymentToDelete) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('payments')
        .delete()
        .eq('id', paymentToDelete.id);
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Обновляем данные после удаления
      handleRefreshData();
      
      toast.success(`Платеж ${paymentToDelete.human_number || paymentToDelete.id.substring(0, 8)} успешно удален`);
    } catch (error: any) {
      console.error('Ошибка при удалении платежа:', error);
      toast.error(`Ошибка при удалении платежа: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setPaymentToDelete(null);
    }
  };
  
  const cancelDeletePayment = () => {
    setIsDeleteDialogOpen(false);
    setPaymentToDelete(null);
  };

  const columns = getPaymentColumns(handleViewRental, handleDownloadReceipt, handleOpenInvoice, handleDeletePayment);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold">Платежи</h1>
        
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {/* Группа настроек просмотра */}
          <div className="flex items-center gap-3 px-3 py-2 bg-muted/30 rounded-lg border">
            <div className="flex items-center gap-2">
              <Switch
                id="pagination-toggle"
                checked={usePagination}
                onCheckedChange={togglePagination}
              />
              <Label htmlFor="pagination-toggle" className="text-sm font-medium cursor-pointer">
                {usePagination ? (
                  <span className="flex items-center gap-1.5">
                    <List className="h-3.5 w-3.5" />
                    Пагинация
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <Database className="h-3.5 w-3.5" />
                    Все данные
                  </span>
                )}
              </Label>
            </div>

            <div className="h-4 w-px bg-border" />

            <div className="flex items-center gap-2">
              <Switch
                id="payment-panel-toggle"
                checked={showUpcomingPayments}
                onCheckedChange={toggleUpcomingPayments}
              />
              <Label htmlFor="payment-panel-toggle" className="text-sm font-medium cursor-pointer">
                {showUpcomingPayments ? (
                  <span className="flex items-center gap-1.5">
                    <Eye className="h-3.5 w-3.5" />
                    По подписке
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <EyeOff className="h-3.5 w-3.5" />
                    По подписке
                  </span>
                )}
              </Label>
            </div>
          </div>
          
          {/* Группа действий */}
          <div className="flex items-center gap-2">
            <PaymentActions 
              fetchPayments={handleRefreshData}
              dataSource={dataSource}
              setStatusFilter={setStatusFilter}
              payments={payments}
            />
          </div>
        </div>
      </div>

      <ConnectionErrorAlert 
        connectionError={connectionError} 
        dataSource={dataSource} 
      />

      {/* Статистика платежей */}
      <PaymentStatsCard 
        payments={payments} 
        totalCount={payments.length}
        loading={loading}
      />

      <div className={`grid grid-cols-1 gap-6 ${showUpcomingPayments ? 'xl:grid-cols-4' : ''}`}>
        {showUpcomingPayments && (
          <div className="xl:col-span-1">
            <UpcomingPaymentsPanel />
          </div>
        )}
        
        <div className={showUpcomingPayments ? "xl:col-span-3" : ""}>
          <div className="space-y-4">
            {/* Серверный поиск для пагинированного режима */}
            {usePagination && (
              <div className="flex items-center gap-4 bg-muted/50 px-4 py-3 rounded-lg">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Поиск платежей по номеру, сумме, статусу, курьеру..."
                      className="pl-10"
                      defaultValue={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    Показано {payments.length} из {totalCount} платежей 
                    {totalCount > 0 && ` (страница ${currentPage})`}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!hasPreviousPage || loading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Назад
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!hasNextPage || loading}
                    >
                      Вперед
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <DataTable
              data={filteredPayments}
              columns={columns}
              searchable={!usePagination} // Отключаем клиентский поиск при пагинации
              searchPlaceholder="Поиск платежей..."
              loading={loading}
            />
          </div>
        </div>
      </div>
      
      {/* Диалог подтверждения удаления платежа */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Подтверждение удаления</DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите удалить платеж{' '}
              <span className="font-medium">
                {paymentToDelete?.human_number || (paymentToDelete?.id && paymentToDelete.id.substring(0, 8))}
              </span>
              {' '}на сумму {paymentToDelete?.amount} ₽?
              <p className="mt-2 text-destructive">
                Это действие нельзя отменить.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={cancelDeletePayment}
              disabled={isDeleting}
            >
              Отмена
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeletePayment}
              disabled={isDeleting}
            >
              {isDeleting ? 'Удаление...' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentsPage;
